import { useContext, useEffect, useState } from "react";
import { AppContext } from "../App";
import { getCurrentYear } from "../Helpers";
import { 
  MdArrowForward, 
  MdVerified, 
  MdTrendingUp, 
  MdBalance, 
  MdSecurity, 
  MdHub, 
  MdCorporateFare, 
  MdAnalytics,
  MdLayers
} from "react-icons/md";
import PageHero from "./PageHero";

export default function AboutUsContent() {
  const appContext = useContext(AppContext);
  const [activeTab, setActiveTab] = useState("vision");

  // Multi-regional advisory centers across Tanzania
  const advisoryRegions = [
    "Dar es Salaam (HQ Link)", "Dodoma (Capital Hub)", "Arusha", "Mwanza", 
    "Geita (Mining Sector Advisory)", "Zanzibar", "Kahama", "Tanga"
  ];

  // Core Value Propositions Mapping
  const coreValues = [
    {
      icon: <MdVerified />,
      title: "Absolute Compliance",
      desc: "Navigating complex TRA frameworks, BRELA filing systems, and statutory laws with zero margins for error."
    },
    {
      icon: <MdTrendingUp />,
      title: "Fiscal Optimization",
      desc: "Restructuring asset allocations, minimizing liability exposure, and identifying underlying corporate growth vectors."
    },
    {
      icon: <MdBalance />,
      title: "Fiduciary Integrity",
      desc: "Maintaining elite confidentiality and transparent corporate reporting protocols that stand global audit scrutiny."
    },
    {
      icon: <MdSecurity />,
      title: "Enterprise Risk Shield",
      desc: "Proactively mitigating corporate operational friction, labor disputes, and complex regulatory penalties."
    }
  ];

  // Firm Principles Matrix
  const firmPrinciples = [
    { id: "01", title: "Evidence-Driven Diagnosis", detail: "Every policy, audit structure, or operational advice we issue is anchored directly on verified empirical performance data." },
    { id: "02", title: "Client-Centric Agility", detail: "We dynamically scale our consultation models to suit mid-tier local enterprises up to multinational conglomerates." },
    { id: "03", title: "Continuous Regulatory Sync", detail: "Our systems remain locked in real-time alignment with unfolding Tanzanian legislative shifts and statutory updates." }
  ];

  const heroData = {
    kicker: "Corporate Profile",
    title: "Architects of Enterprise Resilience & Fiscal Excellence",
    description: "Blessing Management Consultancy is an elite-tier Tanzanian corporate advisory firm delivering integrated tax engineering, regulatory compliance, risk architecture, and human resource solutions."
  }

  return (
    <div className="premium-about-view-wrapper">
      
      {/* SECTION 1: MASTER CORPORATE HERO BREADCRUMB LAYER */}
      <PageHero data= {heroData} />

      {/* SECTION 2: ESTABLISHMENT STORY & FOUNDING BACKGROUND */}
      <section className="about-content-block-pad">
        <div className="container">
          <div className="row align-items-center g-5">
            
            <div className="col-lg-6" data-aos="fade-right">
              <span className="section-inline-kicker">Our Heritage & Genesis</span>
              <h2 className="section-block-title-heavy">Built on the Foundations of Financial Technical Mastery</h2>
              <p className="section-narrative-text">
                Established with a vision to eliminate structural inefficiencies within corporate frameworks, 
                <strong> Blessing Management Consultancy</strong> emerged to bridge the critical gap between rigorous 
                statutory compliance and sustainable profitability for businesses operating within East Africa.
              </p>
              <p className="section-narrative-text">
                From our foundational base in Tanzania, we have systematically engineered a powerhouse team 
                of accredited legal specialists, tier-one tax auditors, and senior organizational psychologists. Together, 
                we shield corporate entities from regulatory vulnerabilities while optimizing operational output.
              </p>
              <div className="cta-action-row-node mt-4">
                <button className="btn-premium-gold-fill" onClick={() => appContext.navTo({ item: "contact" })}>
                  <span>Initiate Corporate Audit</span>
                  <MdArrowForward />
                </button>
              </div>
            </div>

            <div className="col-lg-6">
              <div className="overview-interactive-feature-grid">
                {coreValues.map((value, idx) => (
                  <div className="corporate-value-box" key={idx} data-aos="zoom-in" data-aos-delay={idx * 60}>
                    <div className="value-icon-shield">{value.icon}</div>
                    <h3 className="value-box-heading">{value.title}</h3>
                    <p className="value-box-description">{value.desc}</p>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* SECTION 3: VISION & MISSION SYSTEM (TABBED LAYER) */}
      <section className="about-content-block-pad bg-premium-slate-depth">
        <div className="container">
          <div className="row justify-content-center text-center mb-5" data-aos="fade-up">
            <div className="col-xl-7 col-lg-9">
              <span className="section-inline-kicker">Strategic North Stars</span>
              <h2 className="section-block-title-heavy text-white">The Anchors Behind Our Corporate Advisory Models</h2>
            </div>
          </div>

          <div className="row justify-content-center">
            <div className="col-lg-10">
              
              {/* Tab Selector Buttons */}
              <div className="tab-control-strip-wrapper" data-aos="fade-up">
                <button 
                  className={`tab-toggle-node ${activeTab === "vision" ? "active-node" : ""}`}
                  onClick={() => setActiveTab("vision")}
                >
                  <MdAnalytics /> <span>Our Core Vision</span>
                </button>
                <button 
                  className={`tab-toggle-node ${activeTab === "mission" ? "active-node" : ""}`}
                  onClick={() => setActiveTab("mission")}
                >
                  <MdLayers /> <span>Our Core Mission</span>
                </button>
              </div>

              {/* Dynamic Content Panel View */}
              <div className="tab-viewport-card-box" data-aos="fade-up" data-aos-delay="100">
                {activeTab === "vision" ? (
                  <div className="tab-panel-inner-content text-center">
                    <h3 className="panel-headline-gold">To Be East Africa’s Most Trusted Enterprise Engineering Framework</h3>
                    <p className="panel-paragraph-body">
                      We project a business landscape where corporate entities across Tanzania and the broader East 
                      African market navigate complex global markets with total regulatory immunity, peak capital efficiency, 
                      and highly unified organizational structures.
                    </p>
                  </div>
                ) : (
                  <div className="tab-panel-inner-content text-center">
                    <h3 className="panel-headline-gold">Empowering Corporate Entities Through Exacting Strategic Guidance</h3>
                    <p className="panel-paragraph-body">
                      Our operational mandate is to engineer and supply custom, audit-proof accounting systems, proactive tax management solutions, 
                      and bulletproof corporate governance structures. We equip leadership teams with the deep business intelligence required 
                      to de-risk cross-border investments and protect profit lines.
                    </p>
                  </div>
                )}
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* SECTION 4: FIRM PRINCIPLES MATRIX */}
      <section className="about-content-block-pad">
        <div className="container">
          <div className="row align-items-center g-5">
            
            <div className="col-lg-5" data-aos="fade-right">
              <span className="section-inline-kicker">Operational DNA</span>
              <h2 className="section-block-title-heavy">Our Inviolable Consultation Principles</h2>
              <p className="section-narrative-text">
                We do not deal in assumptions. Blessing Management Consultancy operates under strict professional frameworks 
                that guarantee precision across all tax auditing, administrative advisory, and financial structuring pipelines.
              </p>
              <div className="badge-identity-lock-strip mt-4">
                <div className="mini-badge-node"><MdCorporateFare /> Approved Firm</div>
                <div className="mini-badge-node"><MdVerified /> TRA Compliant System</div>
              </div>
            </div>

            <div className="col-lg-7">
              <div className="principles-linear-timeline">
                {firmPrinciples.map((principle, index) => (
                  <div className="principle-linear-node-card" key={index} data-aos="fade-left" data-aos-delay={index * 80}>
                    <div className="principle-numeric-index-node">{principle.id}</div>
                    <div className="principle-text-packet">
                      <h4 className="principle-node-title">{principle.title}</h4>
                      <p className="principle-node-description">{principle.detail}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* SECTION 5: NATIONAL COVERAGE & HUB STRETCH */}
      <section className="about-content-block-pad bg-light-surface-tint border-top">
        <div className="container">
          <div className="row align-items-center g-5">
            
            <div className="col-lg-6 order-2 order-lg-1" data-aos="zoom-in">
              <div className="tanzania-regional-matrix-grid">
                {advisoryRegions.map((region, idx) => (
                  <div className="regional-hub-pill-node" key={idx}>
                    <MdHub className="hub-radar-icon" />
                    <span className="hub-label-string">{region}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="col-lg-6 order-1 order-lg-2" data-aos="fade-left">
              <span className="section-inline-kicker">Pan-Tanzanian Reach</span>
              <h2 className="section-block-title-heavy">Cross-Border Scope, Deep Local Specialization</h2>
              <p className="section-narrative-text">
                While headquartered out of the commercial core of Dar es Salaam, Blessing Management Consultancy 
                deploys technical audit loops and senior field consultants to key logistical and industrial hubs across the country.
              </p>
              <p className="section-narrative-text">
                From the specialized agricultural zones in Arusha to the high-stakes mining conglomerates located within Geita and Kahama, 
                our localized advisory solutions meet the unique regional compliance challenges faced by modern enterprises.
              </p>
            </div>

          </div>
        </div>
      </section>

    </div>
  );
}